import L from "leaflet";

/** Tile file extension from slide File row (upload-photo uses .jpg). */
export function tileExtensionFromSlideFile(file) {
  if (!file) return "png";
  const name = String(file.name || "").toLowerCase();
  const type = String(file.type || "").toLowerCase();
  if (/\.(jpe?g)$/.test(name) || type.includes("jpeg")) return "jpg";
  if (/\.webp$/.test(name) || type.includes("webp")) return "webp";
  if (/\.png$/.test(name) || type.includes("png")) return "png";
  if (name.startsWith("upload-photo")) return "jpg";
  return "png";
}

/** Leaflet tile URL pattern from slide File row. */
export function tileUrlPatternFromSlideFile(file) {
  if (!file?.url) return "";
  const base = String(file.url).replace(/\/+$/, "");
  const ext = tileExtensionFromSlideFile(file);
  return `${base}/{z}/{x}/{y}.${ext}`;
}

export function tileUrlPatternFromRoot(tileRootUrl, tileExt = "png") {
  const base = String(tileRootUrl || "").replace(/\/+$/, "");
  const ext = String(tileExt || "png").replace(/^\./, "");
  return `${base}/{z}/{x}/{y}.${ext}`;
}

export function isUploadPhotoSlide(slideOrGeoJson) {
  const file = slideOrGeoJson?.Files?.[0];
  const name = String(file?.name || file?.alias || "").toLowerCase();
  return name.startsWith("upload-photo");
}

/**
 * Initial Leaflet view for an upload-photo slide.
 *
 * Goals:
 *  • Open zoomed OUT enough that the whole slide is visible.
 *    Previously we opened at maxNativeZoom-1, which on a deep
 *    pyramid (e.g. maxZoom=8) snapped the user into a corner of
 *    the image instead of showing them the whole thing.
 *  • Centre the view on the actual image content (top-left tile
 *    pyramid corner), not on Leaflet's default geo anchor.
 */
export function uploadPhotoLeafletView(map, maxNativeZoom) {
  let z = Math.max(0, Number(maxNativeZoom));
  if (!Number.isFinite(z)) z = 4;
  /*
    Compute the centre at the slide's native zoom so the unprojection
    is anchored at the same tile across pyramids. Pixel (128, 128) is
    the centre of tile (0, 0) — the top-left tile that always exists
    in our generated pyramid.
  */
  const projectionZoom = z > 0 ? z : 1;
  const center = map.unproject(L.point(128, 128), projectionZoom);
  /*
    Open 1 level in from fully zoomed-out so the slide is visible
    without being postage-stamp-sized, but still small enough that
    the whole image fits on screen. Capped at the pyramid's native
    depth for very shallow slides.
  */
  const viewZoom = Math.min(1, z);
  return {
    center: [center.lat, center.lng],
    zoom: viewZoom,
  };
}

/** Library / title grid cover image (deep-zoom preview tile). */
export function slideThumbnailUrlFromSlide(slide) {
  const file = slide?.Files?.[0];
  if (!file?.url) return null;
  const base = String(file.url).replace(/\/+$/, "");
  const ext = tileExtensionFromSlideFile(file);
  return `${base}/3/3/3.${ext}`;
}
